import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Activity, BarChart3, ChevronDown, ChevronRight, RefreshCw, Search } from "lucide-react"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  smartoltService,
  type SmartOLTXrtgDeviceOlt,
  type SmartOLTXrtgDevices,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { statusDot, XRTG_RANGES, type XrtgKind, type XrtgRange } from "./smartoltXrtgUtils.ts"

const emptyDevices: SmartOLTXrtgDevices = { olts: [], total_onus: 0 }

export function SmartOLTXrtgPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [devices, setDevices] = useState<SmartOLTXrtgDevices>(emptyDevices)
  const [search, setSearch] = useState("")
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [selectedOnuId, setSelectedOnuId] = useState<string | null>(null)
  const [selectedLabel, setSelectedLabel] = useState<string>("")
  const [kind, setKind] = useState<XrtgKind>("traffic")
  const [range, setRange] = useState<XrtgRange>("1hr")
  const [graphUrl, setGraphUrl] = useState<string | null>(null)
  const [seriesRows, setSeriesRows] = useState<any[]>([])
  const [seriesNote, setSeriesNote] = useState<string | null>(null)
  const [graphLoading, setGraphLoading] = useState(false)
  const [graphError, setGraphError] = useState<string | null>(null)

  const loadDevices = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getXrtgDevices(search)
      setDevices({
        olts: Array.isArray(next?.olts) ? next.olts : [],
        total_onus: next?.total_onus || 0,
      })
      if (next?.olts?.length === 1) {
        setExpanded({ [next.olts[0].olt_id]: true })
      }
    } catch {
      toast.error("Failed to load XRTG devices")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [search])

  useEffect(() => {
    loadDevices()
  }, [loadDevices])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadDevices(true)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [loadDevices])

  useEffect(() => {
    if (!selectedOnuId) {
      setGraphUrl(null)
      setSeriesRows([])
      setSeriesNote(null)
      setGraphError(null)
      return
    }
    let cancelled = false
    let objectUrl: string | null = null
    const loadGraph = async () => {
      setGraphLoading(true)
      setGraphError(null)
      setGraphUrl(null)
      setSeriesRows([])
      setSeriesNote(null)
      try {
        const blob = await smartoltService.getXrtgGraphImage(selectedOnuId, { kind, range })
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setGraphUrl(objectUrl)
      } catch {
        try {
          const series = await smartoltService.getXrtgSeries(selectedOnuId, { kind, range })
          if (cancelled) return
          const primary = kind === "traffic" ? series.series.traffic_down : series.series.signal_rx
          const secondary = kind === "traffic" ? series.series.traffic_up : series.series.signal_tx
          if (!primary.length && !secondary.length) {
            setGraphError("Graph telemetry not available for this ONU yet.")
            return
          }
          const byTs = new Map<string, any>()
          for (const point of primary) {
            const row = byTs.get(point.ts) || { ts: point.ts, label: new Date(point.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }
            row.primary = point.value
            byTs.set(point.ts, row)
          }
          for (const point of secondary) {
            const row = byTs.get(point.ts) || { ts: point.ts, label: new Date(point.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }
            row.secondary = point.value
            byTs.set(point.ts, row)
          }
          setSeriesRows([...byTs.values()].sort((a, b) => String(a.ts).localeCompare(String(b.ts))))
          setSeriesNote(series.message || (series.source === "current_snapshot" ? "Showing latest cached SmartOLT snapshot." : null))
        } catch {
          if (!cancelled) setGraphError("Graph telemetry not available for this ONU yet.")
        }
      } finally {
        if (!cancelled) setGraphLoading(false)
      }
    }
    loadGraph()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [selectedOnuId, kind, range])

  const selectedOlt = useMemo(
    () => devices.olts.find((olt) => olt.onus.some((onu) => onu.onu_external_id === selectedOnuId)) || null,
    [devices.olts, selectedOnuId],
  )

  const pickOnu = (onu: SmartOLTXrtgDeviceOlt["onus"][number]) => {
    setSelectedOnuId(onu.onu_external_id)
    setSelectedLabel(onu.label)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">XRTG Telemetry Graphs</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Real-time bandwidth usage and optical link signal charts for monitored interfaces
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 font-semibold text-blue-700 shadow-xs">
            {devices.total_onus} interfaces
          </span>
          <button
            type="button"
            disabled={refreshing}
            onClick={() => loadDevices(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 font-semibold text-slate-700 hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50 transition-colors shadow-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="grid min-w-0 lg:min-h-[600px] lg:grid-cols-[300px_minmax(0,1fr)]">
          {/* Left Sidebar Tree */}
          <div className="border-b border-slate-100 p-3.5 lg:border-b-0 lg:border-r lg:border-r-slate-100 bg-slate-50/50">
            <div className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Interfaces Tree
            </div>
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search interfaces…"
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {loading ? (
              <div className="py-12 text-center">
                <RefreshCw className="mx-auto h-6 w-6 animate-spin text-blue-600 mb-2" />
                <p className="text-xs text-slate-500">Loading interfaces…</p>
              </div>
            ) : devices.olts.length === 0 ? (
              <p className="py-8 text-center text-xs text-slate-500">No configured ONUs found.</p>
            ) : (
              <div className="max-h-72 space-y-2 overflow-y-auto pr-1 lg:max-h-[calc(100vh-18rem)]">
                {devices.olts.map((olt) => {
                  const open = expanded[olt.olt_id] ?? false
                  return (
                    <div key={olt.olt_id} className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-xs">
                      <button
                        type="button"
                        onClick={() => setExpanded((prev) => ({ ...prev, [olt.olt_id]: !open }))}
                        className="flex w-full items-center justify-between px-3 py-2.5 text-left text-xs font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                      >
                        <span className="flex items-center gap-1.5">
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-blue-600" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                          <span className="truncate">{olt.name}</span>
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 font-mono">
                          {olt.count}
                        </span>
                      </button>
                      {open && (
                        <div className="border-t border-slate-100 divide-y divide-slate-100 bg-slate-50/40">
                          {olt.onus.map((onu) => {
                            const isSelected = selectedOnuId === onu.onu_external_id
                            return (
                              <button
                                key={onu.onu_external_id}
                                type="button"
                                onClick={() => pickOnu(onu)}
                                onDoubleClick={() => navigate(SMARTOLT_PATHS.onuDetail(onu.onu_external_id))}
                                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-all ${
                                  isSelected
                                    ? "bg-blue-50 text-blue-700 border-l-2 border-blue-600 font-semibold"
                                    : "text-slate-700 hover:bg-slate-100/60"
                                }`}
                              >
                                <span className={`h-2 w-2 rounded-full ${statusDot(onu.status_kind)}`} />
                                <span className="min-w-0 flex-1 truncate">{onu.name}</span>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Right Graph Console */}
          <div className="flex min-h-[420px] min-w-0 flex-col lg:min-h-[600px]">
            {/* Toolbar */}
            <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:p-3.5">
              <div className="flex gap-1.5 overflow-x-auto sm:flex-wrap">
                {(["traffic", "signal"] as XrtgKind[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setKind(value)}
                    className={`h-8 shrink-0 rounded-lg px-3.5 text-xs font-semibold capitalize transition-all ${
                      kind === value
                        ? "bg-blue-600 text-white shadow-xs"
                        : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-xs"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 overflow-x-auto sm:flex-wrap">
                {XRTG_RANGES.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setRange(item.id)}
                    className={`h-8 shrink-0 rounded-lg px-2.5 text-xs font-semibold transition-all ${
                      range === item.id
                        ? "bg-blue-50 border border-blue-200 text-blue-700 shadow-xs"
                        : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Graph Visual Area */}
            <div className="flex flex-1 flex-col p-3 sm:p-5">
              {!selectedOnuId ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center py-16">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 border border-slate-200 shadow-inner">
                    <BarChart3 className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">No interface selected</p>
                  <p className="text-xs text-slate-500 max-w-sm">
                    Select an ONU/interface from the left tree to inspect live bandwidth traffic and optical power graphs.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <div>
                      <div className="text-base font-bold text-slate-900">{selectedLabel}</div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {selectedOlt && (
                          <>
                            <Link to={SMARTOLT_PATHS.oltDetail(selectedOlt.olt_id)} className="text-blue-600 hover:underline font-medium">
                              {selectedOlt.name}
                            </Link>
                            {" · "}
                          </>
                        )}
                        <Link to={SMARTOLT_PATHS.onuDetail(selectedOnuId)} className="text-blue-600 hover:underline font-medium">
                          View ONU telemetry &amp; config
                        </Link>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                      <Activity className="h-3.5 w-3.5 animate-pulse" />
                      SmartOLT {kind} · {range}
                    </span>
                  </div>

                  {graphLoading ? (
                    <div className="flex flex-1 items-center justify-center py-20">
                      <div className="flex flex-col items-center gap-2 text-slate-500">
                        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                        <span className="text-sm">Fetching graph telemetry…</span>
                      </div>
                    </div>
                  ) : graphUrl ? (
                    <div className="flex flex-1 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <img
                        src={graphUrl}
                        alt={`${kind} ${range}`}
                        className="max-h-[500px] w-full rounded-lg object-contain"
                      />
                    </div>
                  ) : seriesRows.length ? (
                    <div className="flex flex-1 flex-col space-y-2">
                      {seriesNote && (
                        <p className="text-xs text-amber-700 font-medium">{seriesNote}</p>
                      )}
                      <div className="h-[440px] w-full rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={seriesRows} margin={{ top: 12, right: 16, left: -10, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="label" minTickGap={28} tick={{ fill: "#64748B", fontSize: 11 }} />
                            <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                            <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", borderColor: "#CBD5E1", color: "#0F172A", borderRadius: "0.5rem", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }} />
                            <Line
                              type="monotone"
                              dataKey="primary"
                              stroke="#0284C7"
                              strokeWidth={2}
                              dot={false}
                              name={kind === "traffic" ? "Downstream (Mbps)" : "RX Power (dBm)"}
                            />
                            <Line
                              type="monotone"
                              dataKey="secondary"
                              stroke="#6366F1"
                              strokeWidth={2}
                              dot={false}
                              name={kind === "traffic" ? "Upstream (Mbps)" : "TX Power (dBm)"}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-center py-20 text-slate-500 text-sm">
                      {graphError || "Graph telemetry not available for this ONU yet."}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
